import { Button, Input, toast } from "@gotts.ai/ui";
import { Camera, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

import { useRpcRequest } from "../../hooks/useRpcRequest";

import { ConfirmModal } from "./ConfirmModal";

import type { SnapshotControlsProps } from "./types";

interface NamedSnapshot {
  name: string;
  description?: string;
  snapshotId: string;
  blockNumber: string;
  timestamp: string;
}

export function SnapshotControls({
  loading,
  withLoading,
  onAction,
  snapshotId,
  onSnapshot,
  onRevert,
}: SnapshotControlsProps) {
  const { request } = useRpcRequest();
  const [snapshotName, setSnapshotName] = useState("");
  const [snapshotDescription, setSnapshotDescription] = useState("");
  const [namedSnapshots, setNamedSnapshots] = useState<NamedSnapshot[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<NamedSnapshot | null>(null);

  async function handleNamedSnapshot() {
    if (!snapshotName.trim()) return;
    await withLoading("namedSnapshot", async () => {
      const id = (await request("evm_snapshot")) as string;
      const block = (await request("eth_blockNumber")) as string;
      const blockData = await request("eth_getBlockByNumber", [block, false]);
      const blockDataTyped = blockData as { timestamp?: string } | null;
      const timestamp = blockDataTyped?.timestamp
        ? String(parseInt(blockDataTyped.timestamp, 16))
        : String(Math.floor(Date.now() / 1000));
      const blockNum = String(parseInt(block, 16));

      setNamedSnapshots((prev) => [
        ...prev,
        {
          name: snapshotName.trim(),
          description: snapshotDescription.trim() || undefined,
          snapshotId: id,
          blockNumber: blockNum,
          timestamp,
        },
      ]);
      const name = snapshotName.trim();
      setSnapshotName("");
      setSnapshotDescription("");
      toast.success(`Snapshot "${name}" saved (id: ${id})`);
      onAction?.(`Snapshot: ${name}`);
    });
  }

  async function handleRevertNamedSnapshot(snap: NamedSnapshot) {
    await withLoading(`revert-${snap.snapshotId}`, async () => {
      await request("evm_revert", [snap.snapshotId]);
      const newId = (await request("evm_snapshot")) as string;
      setNamedSnapshots((prev) =>
        prev.map((s) =>
          s.snapshotId === snap.snapshotId ? { ...s, snapshotId: newId } : s,
        ),
      );
      toast.success(`Reverted to snapshot "${snap.name}"`);
      onAction?.(`Reverted: ${snap.name}`);
    });
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    setNamedSnapshots((prev) =>
      prev.filter((s) => s.snapshotId !== deleteTarget.snapshotId),
    );
    toast.success(`Deleted snapshot "${deleteTarget.name}"`);
    setDeleteTarget(null);
  }

  return (
    <div className="space-y-4">
      {/* Quick Snapshot / Revert — side by side */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onSnapshot}
          loading={loading === "snapshot"}
          disabled={loading !== null}
          className="btn-success-outline flex-1"
        >
          <Camera size={13} className="mr-1.5" />
          Snapshot
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onRevert}
          loading={loading === "revert"}
          disabled={loading !== null || !snapshotId}
          className="btn-danger flex-1"
          title={!snapshotId ? "Take a snapshot first" : undefined}
        >
          <RotateCcw size={13} className="mr-1.5" />
          Revert
        </Button>
      </div>

      {snapshotId && (
        <p className="text-[10px] text-[var(--color-text-muted)]">
          Last snapshot:{" "}
          <span className="text-gold font-mono">{snapshotId}</span>
        </p>
      )}

      {/* Named Snapshots form */}
      <div className="space-y-2 rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.015)] p-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
          Named Snapshot
        </p>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder="Snapshot name"
            value={snapshotName}
            onChange={(e) => {
              setSnapshotName(e.target.value);
            }}
            className="flex-1"
          />
          <Button
            variant="gold"
            size="sm"
            onClick={handleNamedSnapshot}
            loading={loading === "namedSnapshot"}
            disabled={loading !== null || !snapshotName.trim()}
          >
            Save
          </Button>
        </div>
        <Input
          type="text"
          placeholder="Description (optional)"
          value={snapshotDescription}
          onChange={(e) => {
            setSnapshotDescription(e.target.value);
          }}
          className="w-full"
        />
      </div>

      {/* Named snapshots list */}
      {namedSnapshots.length === 0 && (
        <p className="py-2 text-center text-[10px] text-[var(--color-text-muted)]">
          No named snapshots yet
        </p>
      )}

      {namedSnapshots.length > 0 && (
        <div className="space-y-2">
          {namedSnapshots.map((snap) => (
            <div
              key={snap.snapshotId}
              className="flex items-center gap-3 rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.015)] px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <span className="text-gold font-mono text-xs font-medium">
                  {snap.name}
                </span>
                {snap.description && (
                  <p className="truncate text-[10px] text-[var(--color-text-muted)]">
                    {snap.description}
                  </p>
                )}
                <p className="font-mono text-[10px] text-[var(--color-text-muted)]">
                  Block {snap.blockNumber} &middot;{" "}
                  {new Date(
                    parseInt(snap.timestamp) * 1000,
                  ).toLocaleTimeString()}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRevertNamedSnapshot(snap)}
                  loading={loading === `revert-${snap.snapshotId}`}
                  disabled={loading !== null}
                  className="btn-danger"
                >
                  Revert
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteTarget(snap);
                  }}
                  disabled={loading !== null}
                  className="flex h-8 w-8 items-center justify-center rounded-sm text-[var(--color-text-muted)] transition-colors hover:bg-[rgba(239,68,68,0.1)] hover:text-[var(--color-error)]"
                  title="Delete snapshot"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm modal for delete */}
      <ConfirmModal
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Snapshot"
        description={`Delete snapshot "${deleteTarget?.name ?? ""}"? This cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}
